import api from 'api/config';
import axios from 'axios';

/**
 * Maps Service for Google Maps integration
 * Provides methods to interact with Google Maps API and configuration
 */
export const mapsService = {
  /**
   * Get Google Maps configuration from backend
   * @returns {Promise} Response with Google Maps API key and config
   */
  getConfig: async () => {
    try {
      const response = await api.get('/config/google-maps');
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return {
        success: false,
        error:
          error.response?.data?.error ||
          'Failed to get Google Maps configuration',
        details: error.response?.data?.details,
      };
    }
  },

  /**
   * Load Google Maps JavaScript API dynamically
   * @param {string} apiKey - Google Maps API key
   * @param {Array} libraries - Additional libraries to load (e.g., ['places'])
   * @returns {Promise} Promise that resolves when API is loaded
   */
  loadGoogleMapsAPI: async (apiKey, libraries = ['places']) => {
    return new Promise((resolve, reject) => {
      // Check if Google Maps is already loaded
      if (window.google && window.google.maps && window.google.maps.places) {
        resolve(window.google.maps);
        return;
      }

      // Check if script is already being loaded
      if (document.querySelector('script[src*="maps.googleapis.com"]')) {
        // Wait for existing script to load
        const checkLoaded = () => {
          if (
            window.google &&
            window.google.maps &&
            window.google.maps.places
          ) {
            resolve(window.google.maps);
          } else {
            setTimeout(checkLoaded, 100);
          }
        };
        checkLoaded();
        return;
      }

      // Create and load the script
      const script = document.createElement('script');
      const librariesParam =
        libraries.length > 0 ? `&libraries=${libraries.join(',')}` : '';
      script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}${librariesParam}&loading=async`;
      script.async = true;
      script.defer = true;

      script.onload = () => {
        if (window.google && window.google.maps) {
          resolve(window.google.maps);
        } else {
          reject(new Error('Google Maps API failed to load properly'));
        }
      };

      script.onerror = () => {
        reject(new Error('Failed to load Google Maps API script'));
      };

      document.head.appendChild(script);
    });
  },

  /**
   * Initialize Google Maps API with configuration from backend
   * @param {Array} libraries - Additional libraries to load (default: ['places'])
   * @returns {Promise} Promise that resolves with Google Maps API object
   */
  initialize: async (libraries = ['places']) => {
    try {
      // Get configuration from backend
      const configResponse = await mapsService.getConfig();

      if (!configResponse.success) {
        throw new Error(
          configResponse.error || 'Failed to get Google Maps configuration'
        );
      }

      const { api_key } = configResponse.data;
      if (!api_key) {
        throw new Error('Google Maps API key not configured');
      }

      // Load the Google Maps API
      const googleMaps = await mapsService.loadGoogleMapsAPI(
        api_key,
        libraries
      );

      return {
        success: true,
        data: {
          googleMaps,
          config: configResponse.data,
        },
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to initialize Google Maps API',
      };
    }
  },

  /**
   * Create a PlaceAutocompleteElement instance (new API)
   * @param {Object} options - Autocomplete options
   * @returns {Promise} Promise resolving to autocomplete element
   */
  createAutocomplete: async (options = {}) => {
    try {
      const initResult = await mapsService.initialize(['places']);

      if (!initResult.success) {
        throw new Error(initResult.error);
      }

      const { googleMaps } = initResult.data;

      // Check what's actually available in the places API
      if (!googleMaps.places) {
        throw new Error('Google Maps Places API not loaded');
      }

      // Try to use PlaceAutocompleteElement if available
      if (googleMaps.places.PlaceAutocompleteElement) {
        const autocompleteElement =
          new googleMaps.places.PlaceAutocompleteElement();

        return {
          success: true,
          data: {
            autocomplete: autocompleteElement,
            googleMaps,
            isNewAPI: true,
          },
        };
      }
    } catch (error) {
      console.error('[Maps Service] Error creating autocomplete:', error);
      return {
        success: false,
        error: error.message || 'Failed to create Places Autocomplete',
      };
    }
  },

  /**
   * Get Google Maps configuration from backend
   * @returns {Promise} Response with Google Maps API key and config
   */
  getConfig: async () => {
    try {
      const response = await api.get('/config/google-maps');
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error('Failed to get Google Maps config:', error);
      return {
        success: false,
        error:
          error.response?.data?.error ||
          'Failed to retrieve Google Maps configuration',
      };
    }
  },

  /**
   * Get the Google Maps API key from backend config
   * @returns {Promise<string|null>} API key or null if not found
   */
  getApiKey: async () => {
    try {
      const result = await mapsService.getConfig();
      if (result.success && result.data.api_key) {
        return result.data.api_key;
      }

      console.warn(
        '[Maps Service] Failed to get API key from backend, falling back to environment variable'
      );
      return process.env.REACT_APP_GOOGLE_MAPS_API_KEY || null;
    } catch (error) {
      console.warn('[Maps Service] Error fetching Google Maps config:', error);
      return process.env.REACT_APP_GOOGLE_MAPS_API_KEY || null;
    }
  },

  /**
   * Get place autocomplete suggestions using Places API Web Service
   * @param {string} input - Search input text
   * @param {Object} options - Autocomplete options
   * @returns {Promise<Object>} Result with suggestions or error
   */
  getAutocompleteSuggestions: async (input, options = {}) => {
    try {
      if (!input || input.trim().length < 2) {
        return {
          success: true,
          data: { suggestions: [] },
        };
      }

      const apiKey = await mapsService.getApiKey();
      if (!apiKey) {
        throw new Error('Google Maps API key not configured');
      }

      const requestBody = {
        input: input.trim(),
        ...options,
      };

      // Add location bias if provided
      if (options.locationBias) {
        requestBody.locationBias = options.locationBias;
      }

      // Add included types if provided
      if (options.includedPrimaryTypes) {
        requestBody.includedPrimaryTypes = options.includedPrimaryTypes;
      }

      // Use separate axios instance for external Google Places API call (no credentials)
      const response = await axios({
        method: 'POST',
        url: 'https://places.googleapis.com/v1/places:autocomplete',
        headers: {
          'Content-Type': 'application/json',
          'X-Goog-Api-Key': apiKey,
        },
        data: requestBody,
        withCredentials: false, // Disable credentials for external API
      });

      const data = response.data;

      return {
        success: true,
        data: {
          suggestions: data.suggestions || [],
          raw: data,
        },
      };
    } catch (error) {
      console.error(
        '[Maps Service] Error getting autocomplete suggestions:',
        error
      );
      return {
        success: false,
        error: error.message || 'Failed to get autocomplete suggestions',
      };
    }
  },

  /**
   * Check if Google Maps API is already loaded
   * @returns {boolean} True if Google Maps API is loaded
   */
  isLoaded: () => {
    return !!(window.google && window.google.maps && window.google.maps.places);
  },

  /**
   * Get the current Google Maps API instance (if loaded)
   * @returns {Object|null} Google Maps API object or null
   */
  getGoogleMaps: () => {
    return window.google && window.google.maps ? window.google.maps : null;
  },
};

/**
 * Utility functions for Maps service
 */
export const mapsUtils = {
  /**
   * Format place data for consistent usage
   * @param {Object} place - Google Places API place object
   * @returns {Object} Formatted place data
   */
  formatPlace: (place) => {
    if (!place) {
      return null;
    }

    return {
      formatted_address: place.formatted_address || '',
      name: place.name || '',
      place_id: place.place_id || '',
      geometry: place.geometry || null,
      address_components: place.address_components || [],
    };
  },

  /**
   * Extract specific address components from a place
   * @param {Object} place - Google Places API place object
   * @param {Array} componentTypes - Array of component types to extract
   * @returns {Object} Object with extracted components
   */
  extractAddressComponents: (
    place,
    componentTypes = ['locality', 'administrative_area_level_1', 'country']
  ) => {
    if (!place || !place.address_components) {
      return {};
    }

    const components = {};

    place.address_components.forEach((component) => {
      componentTypes.forEach((type) => {
        if (component.types.includes(type)) {
          components[type] = {
            long_name: component.long_name,
            short_name: component.short_name,
          };
        }
      });
    });

    return components;
  },

  /**
   * Get coordinates from a place object
   * @param {Object} place - Google Places API place object
   * @returns {Object|null} Coordinates object with lat/lng or null
   */
  getCoordinates: (place) => {
    if (!place || !place.geometry || !place.geometry.location) {
      return null;
    }

    const location = place.geometry.location;

    // Handle both LatLng object and function-based location
    if (
      typeof location.lat === 'function' &&
      typeof location.lng === 'function'
    ) {
      return {
        lat: location.lat(),
        lng: location.lng(),
      };
    } else if (
      typeof location.lat === 'number' &&
      typeof location.lng === 'number'
    ) {
      return {
        lat: location.lat,
        lng: location.lng,
      };
    }

    return null;
  },

  /**
   * Validate Google Maps configuration
   * @param {Object} config - Maps configuration object
   * @returns {Object} Validation result
   */
  validateConfig: (config) => {
    const errors = [];

    if (!config) {
      errors.push('Configuration is required');
      return { isValid: false, errors };
    }

    if (!config.api_key || config.api_key.trim().length === 0) {
      errors.push('Google Maps API key is required');
    }

    if (config.api_key && !config.api_key.startsWith('AIza')) {
      errors.push('Invalid Google Maps API key format');
    }

    return {
      isValid: errors.length === 0,
      errors,
    };
  },
};

export default mapsService;
